import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// 작업 유형
export type TaskType = '전사' | '전사검수' | '번역' | '번역검수';

// 언어 코드
export type LanguageCode = 'KO' | 'EN' | 'JA' | 'ZH' | 'ES' | 'FR' | 'DE';

// 작업 진척도
export type TaskProgress = '대기' | '진행중' | '완료' | '검수중' | '반려';

// 작업자 정보
export interface Worker {
  id: string;
  name: string;
  email: string;
  specialties: TaskType[];
  languages: LanguageCode[];
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
}

// CAT 도구 설정
export interface CATToolOptions {
  sourceSubtitle: {
    viewable: boolean;          // 원본 자막 보기 가능
    updatable: boolean;         // 원본 자막 수정 가능
    updateRequired: boolean;    // 원본 자막 수정 필수 (전사검수 등)
  };
  timecodeSync: boolean;        // 타임코드 동기화 유지
  autoQcRequired: boolean;      // 제출시 자동 QC 수행
  mtEnabled: boolean;           // 기계번역 사용 허용
  videoDownloadable: boolean;   // 영상 다운로드 가능
}

// 작업 유형 템플릿
export interface TaskTypeTemplate {
  id: string;                          // e.g., 'translation', 'review_translation'
  pmName: string;                      // PM용 작업 유형명 (예: '번역')
  workerExposedName?: string;          // 작업자에게 노출되는 이름 (nullable, 예: 'Translation Work')
  defaultCatToolOptions: CATToolOptions; // 기본 CAT 도구 설정
}

// Task 인터페이스
export interface Task {
  id: string;
  jobId: string;
  type: TaskType;
  sourceLanguage: LanguageCode;
  targetLanguage?: LanguageCode; // 전사는 타겟 언어가 없음
  worker?: Worker;
  unitPrice: number; // 단가
  workAmount: number; // 작업량 (예: 단어 수, 페이지 수)
  submissionDate?: string; // 제출일 (YYYY-MM-DD)
  progress: TaskProgress;
  dependencies: string[]; // 선행 작업 Task ID들
  startDate?: string;
  endDate?: string;
  notes?: string;
  catToolOptions: CATToolOptions; // CAT 도구 설정
  tags: string[]; // 분류 태그
  pmMemo?: string; // PM 메모
  workerMemo?: string; // 작업자 메모
}

// Job 인터페이스
export interface Job {
  id: string;
  title: string;
  sourceLanguage: LanguageCode;
  targetLanguages: LanguageCode[];
  tasks: Task[];
  createdAt: string;
  deadline: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'active' | 'completed' | 'paused' | 'cancelled';
  totalAmount: number;
  description?: string;
  clientId?: string;
}

// 보기 옵션
export interface ViewOptions {
  layout: 'horizontal' | 'vertical' | 'grid' | 'timeline';
  groupBy: 'language' | 'taskType' | 'worker' | 'progress';
  showInfo: {
    worker: boolean;
    progress: boolean;
    amount: boolean;
    dates: boolean;
    dependencies: boolean;
  };
  filterBy: {
    languages: LanguageCode[];
    taskTypes: TaskType[];
    progress: TaskProgress[];
    workers: string[];
  };
}

// 필터 옵션
export interface JobFilters {
  search: string;
  status: Job['status'][];
  priority: Job['priority'][];
  languages: LanguageCode[];
  dateRange: {
    start: string;
    end: string;
  };
}

// Job Store 인터페이스
export interface JobStore {
  // 데이터
  jobs: Job[];
  workers: Worker[];
  taskTypeTemplates: TaskTypeTemplate[];
  
  // 뷰 상태
  viewOptions: ViewOptions;
  filters: JobFilters;
  
  // 페이지네이션
  currentPage: number;
  pageSize: number;
  
  // 계산된 값들
  filteredJobs: Job[];
  totalPages: number;
  totalCount: number;
  
  // 액션들
  setViewOptions: (options: Partial<ViewOptions>) => void;
  setFilters: (filters: Partial<JobFilters>) => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  resetFilters: () => void;
  
  // Job 관리
  addJob: (job: Omit<Job, 'id'>) => void;
  updateJob: (id: string, updates: Partial<Job>) => void;
  deleteJob: (id: string) => void;
  
  // Task 관리
  updateTask: (jobId: string, taskId: string, updates: Partial<Task>) => void;
}

// 기본 CAT 도구 설정
const defaultCATOptions: CATToolOptions = {
  sourceSubtitle: {
    viewable: true,
    updatable: false,
    updateRequired: false
  },
  timecodeSync: true,
  autoQcRequired: true,
  mtEnabled: false,
  videoDownloadable: false
};

// 작업 유형 템플릿
const taskTypeTemplates: TaskTypeTemplate[] = [
  {
    id: 'transcription',
    pmName: '전사',
    workerExposedName: 'Transcription Work',
    defaultCatToolOptions: {
      ...defaultCATOptions,
      sourceSubtitle: {
        viewable: false,
        updatable: true,
        updateRequired: true
      },
      videoDownloadable: true
    }
  },
  {
    id: 'transcription_review',
    pmName: '전사검수',
    workerExposedName: 'Transcription Review',
    defaultCatToolOptions: {
      ...defaultCATOptions,
      sourceSubtitle: {
        viewable: true,
        updatable: true,
        updateRequired: true
      },
      videoDownloadable: true
    }
  },
  {
    id: 'translation',
    pmName: '번역',
    workerExposedName: 'Translation Work',
    defaultCatToolOptions: {
      ...defaultCATOptions,
      mtEnabled: true
    }
  },
  {
    id: 'translation_review',
    pmName: '번역검수',
    workerExposedName: 'Translation Review',
    defaultCatToolOptions: {
      ...defaultCATOptions,
      sourceSubtitle: {
        viewable: true,
        updatable: true,
        updateRequired: false
      }
    }
  }
];

// 샘플 작업자 데이터
const sampleWorkers: Worker[] = [
  {
    id: 'worker-001',
    name: '김민수',
    email: 'minsu.kim@example.com',
    specialties: ['전사', '전사검수'],
    languages: ['KO', 'EN'],
    level: 'advanced'
  },
  {
    id: 'worker-002',
    name: '이지은',
    email: 'jieun.lee@example.com',
    specialties: ['번역', '번역검수'],
    languages: ['KO', 'EN', 'JA'],
    level: 'expert'
  },
  {
    id: 'worker-003',
    name: '박준호',
    email: 'junho.park@example.com',
    specialties: ['번역'],
    languages: ['KO', 'EN', 'ZH'],
    level: 'intermediate'
  },
  {
    id: 'worker-004',
    name: '최수연',
    email: 'suyeon.choi@example.com',
    specialties: ['번역검수'],
    languages: ['KO', 'EN', 'JA', 'ZH'],
    level: 'expert'
  },
  {
    id: 'worker-005',
    name: '정혜진',
    email: 'hyejin.jung@example.com',
    specialties: ['전사', '번역'],
    languages: ['KO', 'EN', 'ES'],
    level: 'intermediate'
  }
];

// 헬퍼 함수: 작업 유형에 맞는 기본 CAT 도구 옵션 가져오기
const getCATToolOptionsForTaskType = (taskType: TaskType): CATToolOptions => {
  let templateId: string;
  switch (taskType) {
    case '전사':
      templateId = 'transcription';
      break;
    case '전사검수':
      templateId = 'transcription_review';
      break;
    case '번역':
      templateId = 'translation';
      break;
    case '번역검수':
      templateId = 'translation_review';
      break;
    default:
      templateId = 'translation';
  }
  
  return taskTypeTemplates.find(t => t.id === templateId)?.defaultCatToolOptions || defaultCATOptions;
};

// 샘플 Job 데이터
const sampleJobs: Job[] = [
  {
    id: 'job-001',
    title: 'BTS 다큐멘터리 "Beyond The Star" 자막 번역',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA', 'ZH'],
    createdAt: '2024-03-15',
    deadline: '2024-03-25',
    priority: 'high',
    status: 'active',
    totalAmount: 1500000,
    description: 'BTS 다큐멘터리 시리즈의 자막 번역 작업',
    tasks: [
      {
        id: 'task-001-001',
        jobId: 'job-001',
        type: '전사',
        sourceLanguage: 'KO',
        worker: sampleWorkers[0],
        unitPrice: 50,
        workAmount: 5000,
        submissionDate: '2024-03-18',
        progress: '완료',
        dependencies: [],
        startDate: '2024-03-15',
        endDate: '2024-03-17',
        catToolOptions: getCATToolOptionsForTaskType('전사'),
        tags: ['영상', '번역', 'BTS'],
        pmMemo: '고품질 전사 요구됨',
        workerMemo: '정확한 발음과 타이밍에 주의해주세요'
      },
      {
        id: 'task-001-002',
        jobId: 'job-001',
        type: '전사검수',
        sourceLanguage: 'KO',
        worker: sampleWorkers[0],
        unitPrice: 30,
        workAmount: 5000,
        progress: '진행중',
        dependencies: ['task-001-001'],
        startDate: '2024-03-18',
        catToolOptions: getCATToolOptionsForTaskType('전사검수'),
        tags: ['영상', '검수'],
        pmMemo: '전사 품질 검토 필요',
        workerMemo: '오탈자 및 누락된 부분 확인'
      },
      {
        id: 'task-001-003',
        jobId: 'job-001',
        type: '번역',
        sourceLanguage: 'KO',
        targetLanguage: 'EN',
        worker: sampleWorkers[1],
        unitPrice: 120,
        workAmount: 5000,
        progress: '대기',
        dependencies: ['task-001-002'],
        catToolOptions: getCATToolOptionsForTaskType('번역'),
        tags: ['영상', '번역'],
        pmMemo: 'KO→EN 번역 작업',
        workerMemo: '자연스러운 번역을 원합니다'
      },
      {
        id: 'task-001-004',
        jobId: 'job-001',
        type: '번역',
        sourceLanguage: 'KO',
        targetLanguage: 'JA',
        worker: sampleWorkers[1],
        unitPrice: 110,
        workAmount: 5000,
        progress: '대기',
        dependencies: ['task-001-002'],
        catToolOptions: getCATToolOptionsForTaskType('번역'),
        tags: ['영상', '번역'],
        pmMemo: 'KO→JA 번역 작업',
        workerMemo: '일본어 자연스러운 표현 주의'
      },
      {
        id: 'task-001-005',
        jobId: 'job-001',
        type: '번역',
        sourceLanguage: 'KO',
        targetLanguage: 'ZH',
        worker: sampleWorkers[2],
        unitPrice: 100,
        workAmount: 5000,
        progress: '대기',
        dependencies: ['task-001-002'],
        catToolOptions: getCATToolOptionsForTaskType('번역'),
        tags: ['영상', '번역'],
        pmMemo: 'KO→ZH 번역 작업',
        workerMemo: '중국어 간체자 사용'
      },
      {
        id: 'task-001-006',
        jobId: 'job-001',
        type: '번역검수',
        sourceLanguage: 'KO',
        targetLanguage: 'EN',
        worker: sampleWorkers[3],
        unitPrice: 80,
        workAmount: 5000,
        progress: '대기',
        dependencies: ['task-001-003'],
        catToolOptions: getCATToolOptionsForTaskType('번역검수'),
        tags: ['영상', '검수'],
        pmMemo: 'EN 번역 검수',
        workerMemo: '문법과 의미 전달에 집중'
      },
      {
        id: 'task-001-007',
        jobId: 'job-001',
        type: '번역검수',
        sourceLanguage: 'KO',
        targetLanguage: 'JA',
        worker: sampleWorkers[3],
        unitPrice: 75,
        workAmount: 5000,
        progress: '대기',
        dependencies: ['task-001-004'],
        catToolOptions: getCATToolOptionsForTaskType('번역검수'),
        tags: ['영상', '검수'],
        pmMemo: 'JA 번역 검수',
        workerMemo: '일본어 존댓말 확인'
      },
      {
        id: 'task-001-008',
        jobId: 'job-001',
        type: '번역검수',
        sourceLanguage: 'KO',
        targetLanguage: 'ZH',
        worker: sampleWorkers[3],
        unitPrice: 70,
        workAmount: 5000,
        progress: '대기',
        dependencies: ['task-001-005'],
        catToolOptions: getCATToolOptionsForTaskType('번역검수'),
        tags: ['영상', '검수'],
        pmMemo: 'ZH 번역 검수',
        workerMemo: '중국어 번체/간체 확인'
      }
    ]
  },
  {
    id: 'job-002',
    title: 'LE SSERAFIM 인터뷰 영상 다국어 번역',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA'],
    createdAt: '2024-03-14',
    deadline: '2024-03-22',
    priority: 'medium',
    status: 'active',
    totalAmount: 800000,
    description: 'LE SSERAFIM 멤버 인터뷰 영상의 다국어 번역',
    tasks: [
      {
        id: 'task-002-001',
        jobId: 'job-002',
        type: '전사',
        sourceLanguage: 'KO',
        worker: sampleWorkers[4],
        unitPrice: 50,
        workAmount: 3000,
        submissionDate: '2024-03-16',
        progress: '완료',
        dependencies: [],
        startDate: '2024-03-14',
        endDate: '2024-03-15',
        catToolOptions: getCATToolOptionsForTaskType('전사'),
        tags: ['영상', '인터뷰'],
        pmMemo: 'LE SSERAFIM 인터뷰 전사',
        workerMemo: '인터뷰 형식에 맞게 전사'
      },
      {
        id: 'task-002-002',
        jobId: 'job-002',
        type: '전사검수',
        sourceLanguage: 'KO',
        worker: sampleWorkers[0],
        unitPrice: 30,
        workAmount: 3000,
        submissionDate: '2024-03-17',
        progress: '완료',
        dependencies: ['task-002-001'],
        startDate: '2024-03-16',
        endDate: '2024-03-16',
        catToolOptions: getCATToolOptionsForTaskType('전사검수'),
        tags: ['영상', '검수'],
        pmMemo: '전사 품질 검수',
        workerMemo: '오탈자 및 발음 정확성 검토'
      },
      {
        id: 'task-002-003',
        jobId: 'job-002',
        type: '번역',
        sourceLanguage: 'KO',
        targetLanguage: 'EN',
        worker: sampleWorkers[1],
        unitPrice: 120,
        workAmount: 3000,
        progress: '진행중',
        dependencies: ['task-002-002'],
        startDate: '2024-03-17',
        catToolOptions: getCATToolOptionsForTaskType('번역'),
        tags: ['영상', '번역'],
        pmMemo: 'KO→EN 번역',
        workerMemo: '자연스러운 영어 표현 사용'
      },
      {
        id: 'task-002-004',
        jobId: 'job-002',
        type: '번역',
        sourceLanguage: 'KO',
        targetLanguage: 'JA',
        worker: sampleWorkers[1],
        unitPrice: 110,
        workAmount: 3000,
        progress: '진행중',
        dependencies: ['task-002-002'],
        startDate: '2024-03-17',
        catToolOptions: getCATToolOptionsForTaskType('번역'),
        tags: ['영상', '번역'],
        pmMemo: 'KO→JA 번역',
        workerMemo: '일본어 자연스러운 표현 주의'
      },
      {
        id: 'task-002-005',
        jobId: 'job-002',
        type: '번역검수',
        sourceLanguage: 'KO',
        targetLanguage: 'EN',
        worker: sampleWorkers[3],
        unitPrice: 80,
        workAmount: 3000,
        progress: '대기',
        dependencies: ['task-002-003'],
        catToolOptions: getCATToolOptionsForTaskType('번역검수'),
        tags: ['영상', '검수'],
        pmMemo: 'EN 번역 검수',
        workerMemo: '문법과 의미 전달에 집중'
      },
      {
        id: 'task-002-006',
        jobId: 'job-002',
        type: '번역검수',
        sourceLanguage: 'KO',
        targetLanguage: 'JA',
        worker: sampleWorkers[3],
        unitPrice: 75,
        workAmount: 3000,
        progress: '대기',
        dependencies: ['task-002-004'],
        catToolOptions: getCATToolOptionsForTaskType('번역검수'),
        tags: ['영상', '검수'],
        pmMemo: 'JA 번역 검수',
        workerMemo: '일본어 존댓말 확인'
      }
    ]
  },
  {
    id: 'job-003',
    title: 'IVE 신곡 "I AM" 뮤직비디오 자막',
    sourceLanguage: 'KO',
    targetLanguages: ['EN'],
    createdAt: '2024-03-13',
    deadline: '2024-03-20',
    priority: 'urgent',
    status: 'active',
    totalAmount: 450000,
    description: 'IVE 신곡 뮤직비디오의 영어 자막 제작',
    tasks: [
      {
        id: 'task-003-001',
        jobId: 'job-003',
        type: '전사',
        sourceLanguage: 'KO',
        worker: sampleWorkers[4],
        unitPrice: 50,
        workAmount: 1500,
        submissionDate: '2024-03-14',
        progress: '완료',
        dependencies: [],
        startDate: '2024-03-13',
        endDate: '2024-03-13',
        catToolOptions: getCATToolOptionsForTaskType('전사'),
        tags: ['영상', 'IVE', '뮤비'],
        pmMemo: 'IVE 신곡 전사',
        workerMemo: '뮤직비디오 가사 전사'
      },
      {
        id: 'task-003-002',
        jobId: 'job-003',
        type: '전사검수',
        sourceLanguage: 'KO',
        worker: sampleWorkers[0],
        unitPrice: 30,
        workAmount: 1500,
        submissionDate: '2024-03-15',
        progress: '완료',
        dependencies: ['task-003-001'],
        startDate: '2024-03-14',
        endDate: '2024-03-14',
        catToolOptions: getCATToolOptionsForTaskType('전사검수'),
        tags: ['영상', '검수'],
        pmMemo: '전사 품질 검수',
        workerMemo: '오탈자 및 발음 정확성 검토'
      },
      {
        id: 'task-003-003',
        jobId: 'job-003',
        type: '번역',
        sourceLanguage: 'KO',
        targetLanguage: 'EN',
        worker: sampleWorkers[1],
        unitPrice: 120,
        workAmount: 1500,
        submissionDate: '2024-03-18',
        progress: '완료',
        dependencies: ['task-003-002'],
        startDate: '2024-03-15',
        endDate: '2024-03-17',
        catToolOptions: getCATToolOptionsForTaskType('번역'),
        tags: ['영상', '번역', 'IVE'],
        pmMemo: 'KO→EN 번역',
        workerMemo: '자연스러운 영어 표현 사용'
      },
      {
        id: 'task-003-004',
        jobId: 'job-003',
        type: '번역검수',
        sourceLanguage: 'KO',
        targetLanguage: 'EN',
        worker: sampleWorkers[3],
        unitPrice: 80,
        workAmount: 1500,
        progress: '검수중',
        dependencies: ['task-003-003'],
        startDate: '2024-03-18',
        catToolOptions: getCATToolOptionsForTaskType('번역검수'),
        tags: ['영상', '검수'],
        pmMemo: 'EN 번역 검수',
        workerMemo: '문법과 의미 전달에 집중'
      }
    ]
  }
];

// 기본 뷰 옵션
const defaultViewOptions: ViewOptions = {
  layout: 'horizontal',
  groupBy: 'language',
  showInfo: {
    worker: true,
    progress: true,
    amount: true,
    dates: true,
    dependencies: false
  },
  filterBy: {
    languages: [],
    taskTypes: [],
    progress: [],
    workers: []
  }
};

// 기본 필터
const defaultFilters: JobFilters = {
  search: '',
  status: [],
  priority: [],
  languages: [],
  dateRange: {
    start: '',
    end: ''
  }
};

// Job Store 생성
export const useJobStore = create<JobStore>()(
  devtools(
    persist(
      (set, get) => ({
        // 초기 데이터
        jobs: sampleJobs,
        workers: sampleWorkers,
        taskTypeTemplates: taskTypeTemplates,
        viewOptions: defaultViewOptions,
        filters: defaultFilters,
        currentPage: 1,
        pageSize: 10,
        
        // 계산된 값들
        get filteredJobs() {
          const { jobs, filters } = get();
          return jobs.filter(job => {
            // 검색 필터
            if (filters.search) {
              const searchTerm = filters.search.toLowerCase();
              const matchesSearch = 
                job.title.toLowerCase().includes(searchTerm) ||
                job.description?.toLowerCase().includes(searchTerm) ||
                job.id.toLowerCase().includes(searchTerm);
              if (!matchesSearch) return false;
            }
            
            // 상태 필터
            if (filters.status.length > 0 && !filters.status.includes(job.status)) {
              return false;
            }
            
            // 우선순위 필터
            if (filters.priority.length > 0 && !filters.priority.includes(job.priority)) {
              return false;
            }
            
            // 언어 필터
            if (filters.languages.length > 0) {
              const hasMatchingLanguage = 
                filters.languages.includes(job.sourceLanguage) ||
                job.targetLanguages.some(lang => filters.languages.includes(lang));
              if (!hasMatchingLanguage) return false;
            }
            
            // 날짜 범위 필터
            if (filters.dateRange.start && job.createdAt < filters.dateRange.start) return false;
            if (filters.dateRange.end && job.createdAt > filters.dateRange.end) return false;
            
            return true;
          });
        },
        
        get totalPages() {
          const { filteredJobs, pageSize } = get();
          return Math.ceil(filteredJobs.length / pageSize);
        },
        
        get totalCount() {
          return get().filteredJobs.length;
        },
        
        // 액션들
        setViewOptions: (options) => set(state => ({
          viewOptions: { ...state.viewOptions, ...options }
        })),
        
        setFilters: (newFilters) => set(state => ({
          filters: { ...state.filters, ...newFilters },
          currentPage: 1
        })),
        
        setCurrentPage: (page) => set({ currentPage: page }),
        setPageSize: (size) => set({ pageSize: size, currentPage: 1 }),
        
        resetFilters: () => set({
          filters: defaultFilters,
          currentPage: 1
        }),
        
        // Job 관리
        addJob: (jobData) => set(state => ({
          jobs: [...state.jobs, { ...jobData, id: `job-${Date.now()}` }]
        })),
        
        updateJob: (id, updates) => set(state => ({
          jobs: state.jobs.map(job => 
            job.id === id ? { ...job, ...updates } : job
          )
        })),
        
        deleteJob: (id) => set(state => ({
          jobs: state.jobs.filter(job => job.id !== id)
        })),
        
        // Task 관리
        updateTask: (jobId, taskId, updates) => set(state => ({
          jobs: state.jobs.map(job => 
            job.id === jobId 
              ? {
                  ...job,
                  tasks: job.tasks.map(task =>
                    task.id === taskId ? { ...task, ...updates } : task
                  )
                }
              : job
          )
        }))
      }),
      {
        name: 'job-store',
        partialize: (state) => ({
          viewOptions: state.viewOptions,
          filters: state.filters,
          currentPage: state.currentPage,
          pageSize: state.pageSize
        })
      }
    ),
    {
      name: 'job-store'
    }
  )
);
